import React, {
  FC, useMemo, useState, useEffect,
} from 'react';
import {
  useWhatChanged,
  setUseWhatChange,
} from '@simbathesailor/use-what-changed';
import { usePrevious } from 'app/utils/react';
import classnames from 'classnames';
import _ from 'lodash';
import base64 from 'base-64';
import Isvg from 'react-inlinesvg';
import {
  useTable, useAsyncDebounce, useSortBy, usePagination, useRowState,
} from 'react-table';
import history from 'app/routes/history';
import Loader from 'app/components/general/Loader';
import TableFilter from 'app/components/general/TableFilter';
import QuickTableFilter from 'app/components/general/QuickTableFilter';
import DownArrow from 'app/images/icons/down-arrow.svg';

interface IProps {
  data: any[];
  rowState: any[];
  columns: any[];
  isLoading: boolean;
  manualSortBy: boolean;
  fetchDataFromApi?: Function;
  getRowProps?: Function;
  pagination?: object;
  className?: string;
  sectionClassName?: string;
  objectTitle?: string;
  headerTitle?: string;
  filterTitle?: string;
  counterText?: string;
  filterOptions?: any[];
  quickFilterOptions?: any[];
  dragSelected?: { [id: string]: boolean };
  filterConfig?: object;
  persistentFilterConfig?: any;
  children?: React.ReactNode;
  handleClick?: any;
  saveFilters?: boolean;
  filterable?: boolean;
  showPagination?: boolean;
  aggressiveLoader?: boolean;
  manual?: boolean;
  defaultPageSize?: number;
  page?: number;
  rowSelectableContainerId?: string;
  infoText?: string;
  tableState?: any;
}

const Table: FC<IProps> = (props: IProps) => {
  const defaultPagination = { pageIndex: 0, pageSize: props.defaultPageSize || 20 };
  const [filterConfig, setFilterConfig] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({
    filtersApplied: false,
    appliedFilterConfig: {},
  });
  const [appliedSortBy, setAppliedSortBy] = useState([]);
  const [appliedPagination, setAppliedPagination] = useState(defaultPagination);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [changed, setChanged] = useState(false);
  const [searchDelay, setSearchDelay] = useState(false);
  const [filterConfigMerged, setFilterConfigMerged] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const prevFilterConfig = usePrevious(filterConfig);
  const prevPropsFilterConfig = usePrevious(props.filterConfig);
  const prevPropsPersistentFilterConfig = usePrevious(props.persistentFilterConfig);

  const { appliedFilterConfig, filtersApplied } = appliedFilters;

  const toggleFilterOption = (accessor, toggleOption): void => {
    if (!toggleOption) {
      setChanged(true);
      const config = _.omit(
        {
          ...filterConfig,
        },
        accessor,
      );
      setFilterConfig(config);
    }
  };

  const handleFilterToggleChange = (accessor, setting = undefined): void => {
    let toggleValue;
    if (setting === undefined) {
      toggleValue = selectedOptions[accessor] === undefined
        ? true
        : !selectedOptions[accessor];
    } else {
      toggleValue = setting;
    }
    const newSelectedOptions = {
      ...selectedOptions,
      [accessor]: toggleValue,
    };
    setChanged(true);
    toggleFilterOption(accessor, toggleValue);
    setSelectedOptions(newSelectedOptions);
  };

  const handleFilterChange = (e: any, name = null, type = null, selectBox = false) => {
    if (e !== null && _.isFunction(e.persist)) {
      e.persist();
    }
    // console.log(e.target.value, name);
    // console.log(e);
    // console.log(name);
    // console.log(type);
    let config;
    let parsedValue;
    let parsedName;
    if (e === null) {
      parsedName = name;
    } else if (type === 'datetime') {
      parsedName = name;
      parsedValue = _.map(e, (f) => f.toString());
    } else if (type === 'currency') {
      parsedName = name;
      parsedValue = e.formattedValue;
    } else if (e.target === undefined) {
      parsedName = name;
      parsedValue = e;
    } else if (_.isEmpty(e.target.value) || e.target.value === '') {
      parsedName = e.target.name;
      parsedValue = e.target.value;
    } else {
      parsedName = e.target.name;
      parsedValue = e.target.value;
    }
    // console.log(`${parsedName} = ${parsedValue}`);
    if (_.isEmpty(parsedValue) || parsedValue === '') {
      config = _.omit(
        {
          ...filterConfig,
        },
        parsedName,
      );
      if (selectBox) {
        handleFilterToggleChange(parsedName, false);
      }
    } else {
      config = {
        ...filterConfig,
        [parsedName]: parsedValue,
      };
      if (selectBox) {
        handleFilterToggleChange(parsedName, true);
      }
    }
    setChanged(true);
    setFilterConfig(config);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchDelay(false);
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchDelay]);

  const handleQuickFilterChange = (e: any, name = null, type = null) => {
    setSearchDelay(true);

    handleFilterChange(e, name, type, true);
  };

  const clearFilters = () => {
    setChanged(true);
    if (props.saveFilters) {
      setFilterConfig({
        ...props.persistentFilterConfig,
      });
    } else {
      setFilterConfig({
        ...props.filterConfig,
        ...props.persistentFilterConfig,
      });
    }
    setSelectedOptions(_.mapValues(props.filterConfig, () => false));

    // setAppliedFilterConfig([]);
    if (history.location.pathname.split(':')[1] !== undefined) {
      history.push(history.location.pathname.split(':')[0]);
    }
  };

  const applyFilters = () => {
    const newAppliedFilterConfig = _.flatMap(
      filterConfig,
      (value: any, key) => {
        const parsedValue = _.isObject(value) && value.id !== undefined ? value.id : value;
        return { id: key, value: parsedValue };
      },
    );
    if (props.saveFilters && !_.isEmpty(filterConfig)) {
      const filterConfigEncoded = base64.encode(JSON.stringify(filterConfig));
      history.push(`${history.location.pathname.split(':')[0]}:${filterConfigEncoded}`);
    }
    if (!_.isEmpty(filterConfig)) {
      setAppliedFilters({ filtersApplied: true, appliedFilterConfig: newAppliedFilterConfig });
    } else {
      setAppliedFilters({ filtersApplied: true, appliedFilterConfig: {} });
    }
  };

  const handleFilterMenuOpen = (openMenu: boolean) => {
    setFilterMenuOpen(openMenu);
  };

  const manualSortBy = props.manualSortBy == undefined;
  const {
    getTableProps,
    getTableBodyProps,
    // setRowState,
    headerGroups,
    rows,
    prepareRow,
    page,
    state: {
      pageIndex, pageSize, sortBy, filters,
    },
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
  } = useTable({
    columns: props.columns,
    data: props.data,
    manualPagination: true,
    manualSortBy,
    getRowId: (row) => row.id,
    initialState: {
      ...props.tableState,
      pageIndex: appliedPagination.pageIndex,
      pageSize: appliedPagination.pageSize,
      sortBy: appliedSortBy,
      // rowState: props.rowState,
    },
    disableMultiSort: true,
    pageCount: props.pagination?.meta?.totalPages,
    useControlledState: (state) => React.useMemo(
      () => ({
        ...state,
        ...props.tableState,
      }),
      [state, props.tableState],
    ),
  },
  useSortBy,
  usePagination,
  useRowState);

  const fetchDataFromAPIDebounced = useAsyncDebounce(props.fetchDataFromApi, 50);

  // useEffect(() => {
  //   if (props.filterConfig !== undefined || props.persistentFilterConfig !== undefined) {
  //     applyFilters();
  //     // console.log('fetch');
  //     // console.log(appliedFilterConfig);
  //     // props.fetchDataFromApi(props);
  //   } else {
  //     setFiltersApplied(true);
  //   }
  // }, []);

  // useEffect(() => {
  //   _.each(props.rowState, (row) => {
  //     setRowState(row.id, (currentValue) => row);
  //   });
  // }, [props.rowState]);

  const checkFetchData = () => {
    // console.log('----');
    // console.log('filterMenuOpen');
    // console.log(filterMenuOpen);
    // console.log('filterConfigMerged');
    // console.log(filterConfigMerged);
    // console.log('filtersApplied');
    // console.log(filtersApplied);
    // console.log('appliedFilterConfig');
    // console.log(appliedFilterConfig);
    // console.log('appliedPagination');
    // console.log(appliedPagination);
    // console.log('sortBy');
    // console.log(sortBy);
    // console.log('----');
    if (!filterMenuOpen && filterConfigMerged && filtersApplied && !searchDelay) {
      const payload = {
        pageSize: appliedPagination.pageSize,
        pageNumber: appliedPagination.pageIndex,
        sort: appliedSortBy,
        filter: appliedFilterConfig,
      };
      props.fetchDataFromApi(payload);
      // fetchDataFromAPIDebounced(payload);
    }
  };

  // uwc-debug
  useEffect(() => {
    checkFetchData();
  }, [JSON.stringify(appliedFilters),
    filterMenuOpen,
    appliedPagination.pageSize,
    appliedPagination.pageIndex,
    appliedSortBy,
    searchDelay]);

  const resetPagination = () => {
    setAppliedPagination(defaultPagination);
  };

  useEffect(() => {
    // console.log('test');
    // console.log(filterConfigMerged);
    // console.log('filtersApplied');
    // console.log(filtersApplied);
    // console.log(!_.isEqual(prevFilterConfig, filterConfig));
    // console.log(props.filterConfig);
    // console.log(filterConfig);
    // console.log('appliedFilterConfig');
    // console.log(appliedFilterConfig);
    // console.log(JSON.stringify(appliedFilterConfig));
    if (filterConfigMerged && ((!_.isEqual(prevFilterConfig, filterConfig)) || (!filtersApplied && _.isEmpty(props.filterConfig)))) {
      // console.log('apply');
      applyFilters();
      resetPagination();
    }
  }, [filterConfig]);

  useEffect(() => {
    setFilterConfigMerged(true);
  }, [filterConfig]);

  // useEffect(() => {
  //   if (!filtersApplied && appliedFilterConfig !== undefined) {
  //     console.log('set filters');
  //     setFiltersApplied(true);
  //   }
  // }, [JSON.stringify(appliedFilterConfig)]);

  useEffect(() => {
    setAppliedPagination({
      pageIndex,
      pageSize,
    });
  }, [pageIndex, pageSize]);

  useEffect(() => {
    setAppliedSortBy(sortBy);
  }, [JSON.stringify(sortBy)]);

  useEffect(() => {
    if (!changed) {
      const newFilterConfig = {
        ...props.filterConfig,
        ...props.persistentFilterConfig,
      };
      setFilterConfig(newFilterConfig);
      setSelectedOptions(_.mapValues(props.filterConfig, () => true));
    }
  }, [props.filterConfig, props.persistentFilterConfig]);

  const handleClick = (e, id: string, original: any): void => {
    e.persist();
    if (props.handleClick !== undefined) {
      props.handleClick(id, original, e);
    }
  };

  const renderPagination = (): React.ReactNode => (
    <div className="pagination-bottom">
      <div className="pagination">
        <div className="previous">
          <button className="btn btn-thinner" type="button" onClick={() => previousPage()} disabled={!canPreviousPage}>
            Previous
          </button>
        </div>
        <div className="center">
          <div className="page-info">
            <div className="page-jump">
              <input
                className="form-control page-number"
                type="number"
                value={appliedPagination.pageIndex + 1}
                onChange={(e) => {
                  const newPage = e.target.value ? Number(e.target.value) - 1 : 0;
                  gotoPage(newPage);
                }}
              />
              of
              <span className="total-pages">{pageOptions.length}</span>
            </div>
          </div>
        </div>
        <div className="next">
          <button className="btn btn-thinner" type="button" onClick={() => nextPage()} disabled={!canNextPage}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
  const renderTable = (): React.ReactNode => {
    const showLoaders = (props.isLoading && (props.aggressiveLoader || _.isEmpty(props.data)));
    return (
      <div className={classnames(
        'section-table',
        {
          'no-data':
            props.data === undefined || props.data.length === 0,
        },
        {
          'no-pagination': !props.showPagination,
        },
        { 'no-click': props.handleClick === undefined },
        props.className,
      )}
      >
        <div className={classnames('table-table', props.className)} {...getTableProps()}>
          <div className="table-thead">
            {headerGroups.map((headerGroup) => (
              <div className="table-tr" {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <div
                    className={classnames('table-th', column.headerClassName)}
                    {...column.getHeaderProps(column.getSortByToggleProps({ title: undefined }))}
                  >
                    {column.render('Header')}
                    <div className={classnames('sort-arrow', { none: !column.isSorted }, { desc: column.isSorted && column.isSortedDesc }, { asc: column.isSorted && !column.isSortedDesc })}>
                      <Isvg src={DownArrow} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {showLoaders && (
            <div className="table-loading with-margin-top-10">
              <Loader skeleton height={25} count={3} isActive color="grey" />
            </div>
          )}
          {!showLoaders && (
            <div id={props.rowSelectableContainerId} className="table-tbody" {...getTableBodyProps()}>
              {rows.map((row, i) => {
                prepareRow(row);
                return (
                  <div
                    onClick={(e) => handleClick(e, row.original.id, row.original)}
                    className={classnames('table-tr', { 'drag-selected': (props.dragSelected !== undefined && (props.dragSelected[row.original.id] || false)) }, row.original.className)}
                    {...row.getRowProps(props.getRowProps !== undefined ? props.getRowProps(row) : row)}
                    id={`${row.original.id}-row`}
                  >
                    {row.cells.map((cell) => (
                      <div
                        className={classnames('table-td', cell.column.className)}
                        {...cell.getCellProps()}
                      >
                        {cell.render('Cell')}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {(props.showPagination && pageOptions.length > 1) && !showLoaders && renderPagination()}
      </div>
    );
  };

  return (
    <div className={`section-group ${props.sectionClassName}`}>
      <div className={classnames({ 'd-none': !(props.headerTitle !== undefined || props.filterable || props.children !== undefined) })}>

        <div className="row section-title">
          {props.headerTitle !== undefined && (

            <div className="col section-title-col">
              <h3>{props.headerTitle}</h3>
            </div>
          )}
          {props.headerTitle === undefined && props.counterText !== undefined && (
            <div className="col col-12 col-lg-4 col-md-4 text-left">
              <h4 className="grey">
                {props.counterText}
              </h4>
            </div>
          )}
          {props.headerTitle === undefined && props.infoText !== undefined && (
            <div className="col col-12 col-lg-4 col-md-4 text-left">
              <span className="text-grey">
                {props.infoText}
              </span>
            </div>
          )}

          <div className={classnames('col col-12 col-lg-8 col-md-8 btn-flex-group margin-top-10-sm', { 'd-none': !(props.filterable || props.children !== undefined) })}>
            <div className="btn-flex-container float-right">
              {props.children}
              <div className={classnames({ 'd-none': !props.filterable || _.isEmpty(props.filterOptions) })}>
                <TableFilter
                  handleFilterChange={handleFilterChange}
                  handleFilterToggleChange={handleFilterToggleChange}
                  handleFilterMenuOpen={handleFilterMenuOpen}
                  clearFilters={clearFilters}
                  applyFilters={applyFilters}
                  filterConfig={filterConfig}
                  selectedOptions={selectedOptions}
                  persistentFilterConfig={props.persistentFilterConfig}
                  title={props.filterTitle}
                  filterOptions={props.filterOptions}
                />
              </div>
              <div className={classnames('flex-grow-1', { 'd-none': !props.filterable || _.isEmpty(props.quickFilterOptions) })}>
                <QuickTableFilter
                  handleFilterChange={handleQuickFilterChange}
                  handleFilterToggleChange={handleFilterToggleChange}
                  handleFilterMenuOpen={handleFilterMenuOpen}
                  clearFilters={clearFilters}
                  applyFilters={applyFilters}
                  filterConfig={filterConfig}
                  selectedOptions={selectedOptions}
                  persistentFilterConfig={props.persistentFilterConfig}
                  title={props.filterTitle}
                  filterOptions={props.quickFilterOptions}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row with-margin-top-10">
        <div className="col">
          <div className="divider full lesser-margin" />
        </div>
      </div>
      <div className="row">
        <div className="col">
          {props.manual ? renderTable() : renderTable()}
        </div>
      </div>
      <div className={classnames('row with-margin-top-10', { 'd-none': props.isLoading || props.data?.length > 0 })}>
        <div className="col text-center">
          No
          {' '}
          {props.objectTitle}
          {' '}
          found.
        </div>
      </div>
    </div>
  );
};
export default Table;
